import { listingGroup, listingDateValue } from "@/lib/profile-listing-groups";
import Image from "next/image";
import Link from "next/link";
import { Icon } from "./icon";

import {
  formatPriceCompact,
  formatDateOnlyUTC,
  formatInt,
  cityStateZipLine,
} from "@/lib/format";
import { CARD_SHELL_STYLE, PHOTO_SCRIM } from "./profile-card-style";
import type { ProfileListing } from "./profile-types";

/**
 * Profile-page v1 re-skin — one rich listing card (all statuses, not just
 * active). The WHOLE card links to the property detail page
 * (`{basePath}/properties/{mlsListingKey}`).
 *
 * The listing photo renders via `next/image` (lazy by default — below-fold
 * cards): it is `ad_listings.photos[0]`, the same Bridge CloudFront media the
 * property gallery optimizes, and that host is whitelisted in `next.config.ts`
 * remotePatterns. A tokenized placeholder (Icon `home_work`) shows when
 * `photoUrl` is null.
 *
 * Prices/dates render via the shared `lib/format` helpers ('en-US' pinned,
 * date-only strings UTC-pinned — hydration safety). Closed cards prefer
 * `closePrice`, with the differing list price shown struck-through. The badge
 * label is "CLOSED" while the tab filter says "Sold" — deliberate v1 parity.
 *
 * Dark badge/scrim tints derive from `--brand-text` via color-mix (never raw
 * black); text on dark = `--brand-primary-foreground`. No hover animations.
 */

const STATUS_BADGE: Record<
  ProfileListing["status"],
  { label: string; background: string; color: string }
> = {
  active: {
    label: "ACTIVE",
    background: "var(--brand-success)",
    color: "var(--brand-primary-foreground)",
  },
  pending: {
    label: "PENDING",
    background: "var(--brand-sun)",
    color: "var(--brand-text)",
  },
  closed: {
    label: "CLOSED",
    background: "color-mix(in srgb, var(--brand-text) 92%, transparent)",
    color: "var(--brand-primary-foreground)",
  },
};

export function ListingCard({
  listing,
  basePath,
}: {
  listing: ProfileListing;
  basePath: string;
}) {
  const badge = STATUS_BADGE[listing.status];
  const isRental = ["rentals", "rented"].includes(listingGroup(listing) ?? "");
  const isClosed = listing.status === "closed";

  // Closed cards show the close price when present; everything else the list
  // price. The struck list price renders only when the two differ.
  const displayPrice =
    isClosed && listing.closePrice !== null ? listing.closePrice : listing.price;
  const struckListPrice =
    isClosed &&
    listing.closePrice !== null &&
    listing.price !== null &&
    listing.closePrice !== listing.price
      ? listing.price
      : null;

  const cityLine = cityStateZipLine(listing.city, listing.state, listing.zip);
  const propertyTypeLabel = listing.propertySubType ?? listing.propertyType;

  // "Sold Mar 24, 2026 · 206 days on market" — either half stands alone.
  const soldDateText = listingDateValue(listing.soldDate) === null ? null : formatDateOnlyUTC(listing.soldDate);
  const soldLine = isClosed
    ? [
        soldDateText ? `${isRental ? "Rented" : "Sold"} ${soldDateText}` : null,
        listing.daysOnMarket !== null
          ? `${listing.daysOnMarket} days on market`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <Link
      href={`${basePath}/properties/${listing.mlsListingKey}`}
      className="block overflow-hidden"
      style={CARD_SHELL_STYLE}
    >
      {/* Photo — next/image (Bridge CloudFront host, whitelisted). `fill`
          inside the relative aspect-[4/3] box; sized to the 1/2-col card grid. */}
      <div
        className="relative aspect-[4/3]"
        style={{ backgroundColor: "var(--brand-surface-subtle)" }}
      >
        {listing.photoUrl ? (
          <Image
            src={listing.photoUrl}
            alt={listing.address ?? "Listing photo"}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 360px"
            className="object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <Icon
              name="home_work"
              size={40}
              style={{ color: "var(--brand-input-placeholder)" }}
            />
          </div>
        )}

        {/* Status + photo-count badges */}
        <div className="absolute left-3 top-3 flex gap-2">
          <span
            className="rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide"
            style={{ backgroundColor: badge.background, color: badge.color }}
          >
            {isClosed && isRental ? "RENTED" : badge.label}
          </span>
          {listing.photoCount > 0 && (
            <span
              className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
              style={{
                backgroundColor:
                  "color-mix(in srgb, var(--brand-text) 70%, transparent)",
                color: "var(--brand-primary-foreground)",
              }}
            >
              {listing.photoCount} photos
            </span>
          )}
        </div>

        {/* Bottom price overlay on a dark scrim */}
        {displayPrice !== null && (
          <div
            className="absolute inset-x-0 bottom-0 px-4 pb-3 pt-10"
            style={{ background: PHOTO_SCRIM }}
          >
            <p
              className="text-2xl font-bold"
              style={{ color: "var(--brand-primary-foreground)" }}
            >
              {formatPriceCompact(displayPrice)}
            </p>
            {struckListPrice !== null && (
              <p
                className="text-[13px] line-through"
                style={{
                  color:
                    "color-mix(in srgb, var(--brand-primary-foreground) 75%, transparent)",
                }}
              >
                {formatPriceCompact(struckListPrice)} list
              </p>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4">
        <p
          className="truncate text-[15px] font-semibold"
          style={{
            color: listing.address ? "var(--brand-text)" : "var(--brand-text-muted)",
          }}
          title={listing.address ?? undefined}
        >
          {listing.address ?? "Address unavailable"}
        </p>

        {cityLine && (
          <p
            className="text-[11.5px] uppercase tracking-wide"
            style={{ color: "var(--brand-text-muted)" }}
          >
            {cityLine}
          </p>
        )}

        {/* Specs row — each part only when present */}
        {(listing.beds !== null ||
          listing.baths !== null ||
          listing.sqft !== null ||
          listing.yearBuilt !== null) && (
          <div
            className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]"
            style={{ color: "var(--brand-text)" }}
          >
            {listing.beds !== null && (
              <span className="inline-flex items-center gap-1">
                <Icon
                  name="bed"
                  size={15}
                  style={{ color: "var(--brand-text-muted)" }}
                />
                {listing.beds} bd
              </span>
            )}
            {listing.baths !== null && (
              <span className="inline-flex items-center gap-1">
                <Icon
                  name="bathtub"
                  size={15}
                  style={{ color: "var(--brand-text-muted)" }}
                />
                {listing.baths} ba
              </span>
            )}
            {listing.sqft !== null && (
              <span className="inline-flex items-center gap-1">
                <Icon
                  name="square_foot"
                  size={15}
                  style={{ color: "var(--brand-text-muted)" }}
                />
                {formatInt(listing.sqft)} sqft
              </span>
            )}
            {listing.yearBuilt !== null && (
              <span style={{ color: "var(--brand-text-muted)" }}>
                Built {listing.yearBuilt}
              </span>
            )}
          </div>
        )}

        {propertyTypeLabel && (
          <p
            className="mt-2 text-[10.5px] font-medium uppercase tracking-[0.06em]"
            style={{ color: "var(--brand-text-muted)" }}
          >
            {propertyTypeLabel}
          </p>
        )}

        {soldLine && (
          <p
            className="mt-1.5 text-[12px]"
            style={{ color: "var(--brand-text-muted)" }}
          >
            {soldLine}
          </p>
        )}
      </div>
    </Link>
  );
}
